import React from 'react';

import { link } from '@aline/routes';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { CircleStop, Pause, Play, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import {
  BACKGROUND_TASK_ICON_SIZE,
  BackgroundTaskPanel,
  BackgroundTaskPanelAction,
} from 'src/components/background-tasks';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { FileTypeIcon } from 'src/components/StorableIcon';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { formatBytes } from './helpers/utils/formatBytes';
import type { UploadFile } from './useRepositoryDropzone';

interface RepositoryDropzoneUploadProgressProps {
  isUploadPaused: boolean;
  onCancelActiveUploads: () => void;
  onClose: () => void;
  onDeleteFile: (fileId: string) => Promise<void>;
  onPauseUploads: () => void;
  onRestoreFile: (fileId: string) => Promise<void>;
  onResumeUploads: () => void;
  onRetryFile: (fileId: string) => Promise<void>;
  uploadFiles: UploadFile[];
}

interface RepositoryDropzoneUploadFileRowProps {
  file: UploadFile;
  onDeleteFile: (fileId: string) => Promise<void>;
  onRestoreFile: (fileId: string) => Promise<void>;
  onRetryFile: (fileId: string) => Promise<void>;
}

const ROW_ICON_SIZE = 16;
const ACTION_ICON_SIZE = 14;

const useUploadProgressStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
      color: theme.appColors.iconsType,
    },
    color: theme.appColors.secondaryType,
    padding: theme.spacing(0.5),
  },
  errorLine: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 12,
    minWidth: 0,
  },
  fileList: {
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 320,
    overflowY: 'auto',
    padding: theme.spacing(0.5),
  },
  fileMeta: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
    fontSize: 10,
    whiteSpace: 'nowrap',
  },
  fileRow: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    borderRadius: theme.spacing(0.75),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.5, 0.75),
  },
  fileRowClickable: {
    cursor: 'pointer',
  },
  fileRowMain: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 24,
    minWidth: 0,
  },
  filename: {
    flex: 1,
    fontSize: 13,
    minWidth: 0,
  },
  filenameTrashed: {
    color: theme.appColors.secondaryType,
    textDecoration: 'line-through',
  },
  metaError: {
    color: theme.palette.error.main,
  },
  metaSuccess: {
    color: theme.palette.success.main,
  },
  retryButton: {
    '&:hover': {
      textDecoration: 'underline',
    },
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: theme.appColors.primary,
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    gap: theme.spacing(0.5),
    padding: 0,
  },
  rowIcon: {
    flexShrink: 0,
  },
}));

export function RepositoryDropzoneUploadProgress({
  isUploadPaused,
  onCancelActiveUploads,
  onClose,
  onDeleteFile,
  onPauseUploads,
  onRestoreFile,
  onResumeUploads,
  onRetryFile,
  uploadFiles,
}: RepositoryDropzoneUploadProgressProps): JSX.Element {
  const { classes } = useUploadProgressStyles();
  const hasActiveUploads = uploadFiles.some(f => f.state === 'pending' || f.state === 'uploading');

  return (
    <BackgroundTaskPanel
      actions={
        hasActiveUploads && (
          <UploadHeaderActions
            isUploadPaused={isUploadPaused}
            onCancelActiveUploads={onCancelActiveUploads}
            onPauseUploads={onPauseUploads}
            onResumeUploads={onResumeUploads}
          />
        )
      }
      count={uploadFiles.length}
      title={getPanelTitle(uploadFiles, isUploadPaused)}
      onClose={onClose}
    >
      <Box className={classes.fileList}>
        {uploadFiles.map(file => (
          <RepositoryDropzoneUploadFileRow
            key={file.id}
            file={file}
            onDeleteFile={onDeleteFile}
            onRestoreFile={onRestoreFile}
            onRetryFile={onRetryFile}
          />
        ))}
      </Box>
    </BackgroundTaskPanel>
  );
}

function UploadHeaderActions({
  isUploadPaused,
  onCancelActiveUploads,
  onPauseUploads,
  onResumeUploads,
}: Pick<
  RepositoryDropzoneUploadProgressProps,
  'isUploadPaused' | 'onCancelActiveUploads' | 'onPauseUploads' | 'onResumeUploads'
>): JSX.Element {
  return (
    <>
      <PauseResumeAction
        isUploadPaused={isUploadPaused}
        onPauseUploads={onPauseUploads}
        onResumeUploads={onResumeUploads}
      />
      <BackgroundTaskPanelAction label="Cancel uploads" onClick={onCancelActiveUploads}>
        <CircleStop size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
      </BackgroundTaskPanelAction>
    </>
  );
}

function PauseResumeAction({
  isUploadPaused,
  onPauseUploads,
  onResumeUploads,
}: Pick<
  RepositoryDropzoneUploadProgressProps,
  'isUploadPaused' | 'onPauseUploads' | 'onResumeUploads'
>): JSX.Element {
  if (isUploadPaused)
    return (
      <BackgroundTaskPanelAction label="Resume uploads" onClick={onResumeUploads}>
        <Play size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
      </BackgroundTaskPanelAction>
    );

  return (
    <BackgroundTaskPanelAction label="Pause uploads" onClick={onPauseUploads}>
      <Pause size={BACKGROUND_TASK_ICON_SIZE} strokeWidth={2} />
    </BackgroundTaskPanelAction>
  );
}

function getPanelTitle(uploadFiles: UploadFile[], isUploadPaused: boolean): string {
  const inProgressCount = uploadFiles.filter(
    f => f.state === 'pending' || f.state === 'uploading' || f.state === 'persisting'
  ).length;
  if (inProgressCount > 0 && isUploadPaused) return 'Uploads paused';
  if (inProgressCount > 0)
    return `Uploading ${inProgressCount} ${inProgressCount === 1 ? 'file' : 'files'}…`;
  if (uploadFiles.some(f => f.state === 'completed')) return 'Upload complete';

  return 'No uploads';
}

function RepositoryDropzoneUploadFileRow({
  file,
  onDeleteFile,
  onRestoreFile,
  onRetryFile,
}: RepositoryDropzoneUploadFileRowProps): JSX.Element {
  const { classes, cx } = useUploadProgressStyles();

  const isUploadingPhase = file.state === 'pending' || file.state === 'uploading';
  const isPersisting = file.state === 'persisting';
  const isFailed = file.state === 'failed';
  const isCompleted = file.state === 'completed';
  const isTrashed = file.isTrashed === true;

  const statusText = isUploadingPhase
    ? `${Math.round(file.progress || 0)}%`
    : isPersisting
      ? 'Saving…'
      : isCompleted
        ? isTrashed
          ? 'Deleted'
          : 'Done'
        : file.state === 'cancelled'
          ? 'Cancelled'
          : isFailed
            ? 'Failed'
            : '';
  const metaText = [file.fileSize ? formatBytes(file.fileSize) : null, statusText]
    .filter(Boolean)
    .join(' · ');

  const openableDocumentId = isCompleted ? file.documentId : undefined;
  const handleRowClick = () => {
    if (!openableDocumentId) return;
    openLink(link.DocumentDetail({ documentId: openableDocumentId }), { title: file.filename });
  };

  return (
    <Box
      className={cx(classes.fileRow, Boolean(openableDocumentId) && classes.fileRowClickable)}
      onClick={handleRowClick}
    >
      <Box className={classes.fileRowMain}>
        <UploadFileRowIcon file={file} />
        <AlineTooltip arrow placement="top" title={file.filename}>
          <Typography noWrap className={cx(classes.filename, isTrashed && classes.filenameTrashed)}>
            {file.filename}
          </Typography>
        </AlineTooltip>
        <Typography
          className={cx(
            classes.fileMeta,
            isFailed && classes.metaError,
            isCompleted && !isTrashed && classes.metaSuccess
          )}
          component="span"
        >
          {metaText}
        </Typography>
        {isCompleted && file.documentId && !isTrashed && (
          <AlineTooltip arrow placement="top" title="Move to trash">
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={e => {
                e.stopPropagation();
                void onDeleteFile(file.id);
              }}
            >
              <Trash2 size={ACTION_ICON_SIZE} strokeWidth={2} />
            </IconButton>
          </AlineTooltip>
        )}
        {isCompleted && file.documentId && isTrashed && (
          <AlineTooltip arrow placement="top" title="Restore">
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={e => {
                e.stopPropagation();
                void onRestoreFile(file.id);
              }}
            >
              <Undo2 size={ACTION_ICON_SIZE} strokeWidth={2} />
            </IconButton>
          </AlineTooltip>
        )}
      </Box>

      {isFailed && (
        <Box className={classes.errorLine}>
          <Typography noWrap className={classes.errorText} variant="caption">
            {file.error ?? 'Upload failed'}
          </Typography>
          <Typography
            className={classes.retryButton}
            component="button"
            variant="caption"
            onClick={() => onRetryFile(file.id)}
          >
            <RotateCcw size={11} strokeWidth={2} />
            Retry
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function UploadFileRowIcon({ file }: { file: UploadFile }): JSX.Element {
  const { classes } = useUploadProgressStyles();
  const isPersisting = file.state === 'persisting';
  const isUploadingPhase = file.state === 'pending' || file.state === 'uploading';

  if (isUploadingPhase || isPersisting) {
    return (
      <CircularProgress
        className={classes.rowIcon}
        size={ROW_ICON_SIZE}
        thickness={5}
        value={isUploadingPhase ? file.progress || 0 : undefined}
        variant={isUploadingPhase ? 'determinate' : 'indeterminate'}
      />
    );
  }

  return (
    <FileTypeIcon
      iconProps={{ className: classes.rowIcon, height: ROW_ICON_SIZE, width: ROW_ICON_SIZE }}
      mimetype={file.mimeType || 'application/octet-stream'}
    />
  );
}
